import React, { useState, useEffect } from "react";
import { Tournament, Round } from "../types/tournament";
import {
  createInitialRound,
  createNextRound,
  updateTeamStats,
  calculateTournamentStats,
  updateCourt1Streak,
  findMatchOfTheRound,
} from "../utils/tournamentLogic";
import CourtDisplay from "./CourtDisplay";
import StandingsBoard from "./StandingsBoard";
import ScoreEntry from "./ScoreEntry";
import TournamentStats from "./TournamentStats";
import LiveDashboard from "./LiveDashboard";
import AchievementDisplay from "./AchievementDisplay";
import PathToCourt1Tracker from "./PathToCourt1Tracker";
import QRCodeDisplay from "./QRCodeDisplay";
import {
  Play,
  RotateCcw,
  Trophy,
  BarChart3,
  Users,
  Monitor,
  Award,
  Target,
  QrCode,
  X,
  Maximize2,
} from "lucide-react";

interface TournamentDashboardProps {
  tournament: Tournament;
  setTournament: (tournament: Tournament) => void;
  onReset: () => void;
}

const TournamentDashboard: React.FC<TournamentDashboardProps> = ({
  tournament,
  setTournament,
  onReset,
}) => {
  const [activeView, setActiveView] = useState<
    "courts" | "standings" | "stats" | "live" | "achievements" | "path" | "qr"
  >("courts");
  const [selectedMatch, setSelectedMatch] = useState<string | null>(null);
  const [showLiveDashboard, setShowLiveDashboard] = useState(false);
  const [liveScores, setLiveScores] = useState<{
    [key: string]: { team1: number; team2: number };
  }>({});

  const currentRound = tournament.rounds[tournament.currentRound - 1];
  const stats = calculateTournamentStats(tournament);

  useEffect(() => {
    if (tournament.rounds.length === 0 && tournament.teams.length > 0) {
      const initialRound = createInitialRound(
        tournament.teams,
        tournament.courts.length
      );
      const updatedTournament = {
        ...tournament,
        rounds: [initialRound],
      };
      setTournament(updatedTournament);
    }
  }, [
    tournament.rounds.length,
    tournament.teams.length,
    tournament.courts.length,
    setTournament,
  ]);

  // Auto-refresh live dashboard every 5 seconds
  useEffect(() => {
    if (showLiveDashboard) {
      const interval = setInterval(() => {
        // Force re-render of live dashboard
        setTournament({ ...tournament });
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [showLiveDashboard, tournament, setTournament]);

  const startNextRound = () => {
    if (!currentRound) return;

    if (!checkRoundComplete()) return;

    const nextRound = createNextRound(
      tournament.teams,
      currentRound,
      tournament.courts.length
    );

    // Update teams with their new court positions
    const updatedTeams = tournament.teams.map((team) => {
      const match = currentRound.matches.find(
        (m) => m.team1.id === team.id || m.team2.id === team.id
      );

      if (!match || !match.result) return team;

      const isWinner = match.result.winner === team.id;
      const currentCourt = team.currentCourt;

      if (isWinner && currentCourt > 1) {
        return {
          ...team,
          currentCourt: currentCourt - 1,
          totalMovement: team.totalMovement - 1,
        };
      } else if (!isWinner && currentCourt < tournament.courts.length) {
        return {
          ...team,
          currentCourt: currentCourt + 1,
          totalMovement: team.totalMovement + 1,
        };
      }

      return team;
    });

    const updatedRounds = [...tournament.rounds, nextRound];
    const updatedTournament = {
      ...tournament,
      teams: updatedTeams,
      rounds: updatedRounds,
      currentRound: tournament.currentRound + 1,
      updatedAt: new Date(),
    };

    setTournament(updatedTournament);
  };

  const updateMatchResult = (
    matchId: string,
    team1Score: number,
    team2Score: number
  ) => {
    if (!currentRound) return;

    const updatedMatches = currentRound.matches.map((match) => {
      if (match.id === matchId) {
        const result = {
          team1Score,
          team2Score,
          winner: team1Score > team2Score ? match.team1.id : match.team2.id,
          loser: team1Score > team2Score ? match.team2.id : match.team1.id,
          completed: true,
          isPerfectGame:
            (team1Score === 11 && team2Score === 0) ||
            (team2Score === 11 && team1Score === 0),
          isCloseMatch: Math.abs(team1Score - team2Score) <= 2,
        };

        return { ...match, result };
      }
      return match;
    });

    // Update team stats and court 1 streaks
    const match = updatedMatches.find((m) => m.id === matchId);
    if (match && match.result) {
      let updatedTeams = updateTeamStats(tournament.teams, match.result);
      updatedTeams = updateCourt1Streak(updatedTeams);

      // Find match of the round
      const matchOfTheRound = findMatchOfTheRound(updatedMatches);

      const isRoundComplete = updatedMatches.every(
        (match) => match.isBye || (match.result && match.result.completed)
      );

      const updatedRound = {
        ...currentRound,
        matches: updatedMatches,
        matchOfTheRound,
        completed: isRoundComplete,
      };
      const updatedRounds = tournament.rounds.map((round, index) =>
        index === tournament.currentRound - 1 ? updatedRound : round
      );

      const updatedTournament = {
        ...tournament,
        teams: updatedTeams,
        rounds: updatedRounds,
        updatedAt: new Date(),
      };

      setTournament(updatedTournament);

      // Clear live scores for this match
      const newLiveScores = { ...liveScores };
      delete newLiveScores[matchId];
      setLiveScores(newLiveScores);
    }
  };

  const updateLiveScore = (
    matchId: string,
    team1Score: number,
    team2Score: number
  ) => {
    setLiveScores((prev) => ({
      ...prev,
      [matchId]: { team1: team1Score, team2: team2Score },
    }));
  };

  const checkRoundComplete = () => {
    if (!currentRound) return false;

    // Check if round is already marked as completed
    if (currentRound.completed) return true;

    // If there are no matches, round is not complete
    if (currentRound.matches.length === 0) return false;

    // Count non-bye matches that need to be played
    const activeMatches = currentRound.matches.filter((match) => !match.isBye);

    // If there are no active matches to play, round should not be auto-complete
    // unless specifically marked as complete
    if (activeMatches.length === 0) return false;

    // Check if all active matches are completed
    const allActiveMatchesComplete = activeMatches.every(
      (match) => match.result && match.result.completed
    );

    return allActiveMatchesComplete;
  };

  const isTournamentComplete = () => {
    return tournament.currentRound > tournament.totalRounds;
  };

  // Full-screen live dashboard
  if (showLiveDashboard) {
    return (
      <div className="fixed inset-0 z-50 bg-black overflow-y-auto">
        <div className="relative w-full min-h-full">
          {/* Close button */}
          <button
            onClick={() => setShowLiveDashboard(false)}
            className="fixed top-4 right-4 z-10 bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-all duration-200"
          >
            <X className="w-6 h-6" />
          </button>

          <LiveDashboard
            tournament={tournament}
            showLiveScores={true}
            liveScores={liveScores}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                🏓 {tournament.name}
              </h1>
              <p className="text-xs sm:text-sm text-gray-600">
                Round {tournament.currentRound} of {tournament.totalRounds} •{" "}
                {tournament.teams.length} Teams • {tournament.courts.length}{" "}
                Courts
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
              {/* Live Dashboard Toggle */}
              <button
                onClick={() => setShowLiveDashboard(true)}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl text-sm"
              >
                <Monitor className="w-4 h-4" />
                <span>Live Display</span>
                <Maximize2 className="w-4 h-4" />
              </button>

              <button
                onClick={onReset}
                className="w-full sm:w-auto bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center justify-center space-x-2 text-sm"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Reset</span>
              </button>
            </div>
          </div>

          {/* Navigation */}
          <div className="bg-white rounded-lg shadow-lg">
            <div className="border-b border-gray-200">
              <nav className="flex flex-wrap sm:flex-nowrap space-x-1 sm:space-x-4 px-4 sm:px-6 overflow-x-auto">
                <button
                  onClick={() => setActiveView("courts")}
                  className={`py-3 sm:py-4 px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center whitespace-nowrap flex-shrink-0 ${
                    activeView === "courts"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Play className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Courts</span>
                  <span className="sm:hidden">Courts</span>
                </button>
                <button
                  onClick={() => setActiveView("standings")}
                  className={`py-3 sm:py-4 px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center whitespace-nowrap flex-shrink-0 ${
                    activeView === "standings"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Standings</span>
                  <span className="sm:hidden">Stand</span>
                </button>
                <button
                  onClick={() => setActiveView("stats")}
                  className={`py-3 sm:py-4 px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center whitespace-nowrap flex-shrink-0 ${
                    activeView === "stats"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Statistics</span>
                  <span className="sm:hidden">Stats</span>
                </button>
                <button
                  onClick={() => setActiveView("achievements")}
                  className={`py-3 sm:py-4 px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center whitespace-nowrap flex-shrink-0 ${
                    activeView === "achievements"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Award className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Achievements</span>
                  <span className="sm:hidden">Awards</span>
                </button>
                <button
                  onClick={() => setActiveView("path")}
                  className={`py-3 sm:py-4 px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center whitespace-nowrap flex-shrink-0 ${
                    activeView === "path"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Target className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Path to Court 1</span>
                  <span className="sm:hidden">Path</span>
                </button>
                <button
                  onClick={() => setActiveView("qr")}
                  className={`py-3 sm:py-4 px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center whitespace-nowrap flex-shrink-0 ${
                    activeView === "qr"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <QrCode className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">QR Code</span>
                  <span className="sm:hidden">QR</span>
                </button>
              </nav>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6">
              {activeView === "courts" && (
                <div className="space-y-6">
                  {/* Round Status */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                    <div>
                      <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                        Round {tournament.currentRound}
                      </h2>
                      <p className="text-sm sm:text-base text-gray-600">
                        {checkRoundComplete()
                          ? "Round complete! Ready for next round."
                          : "Matches in progress..."}
                      </p>
                    </div>
                    {checkRoundComplete() && !isTournamentComplete() && (
                      <button
                        onClick={startNextRound}
                        className="w-full sm:w-auto bg-primary-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors duration-200 text-sm sm:text-base"
                      >
                        Start Next Round
                      </button>
                    )}
                  </div>

                  {/* Courts Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                    {currentRound?.matches.map((match) => (
                      <CourtDisplay
                        key={match.id}
                        match={match}
                        onEnterScore={() => setSelectedMatch(match.id)}
                        liveScore={liveScores[match.id]}
                        onLiveScoreUpdate={updateLiveScore}
                      />
                    ))}
                  </div>
                </div>
              )}

              {activeView === "standings" && (
                <StandingsBoard teams={tournament.teams} />
              )}

              {activeView === "stats" && (
                <TournamentStats stats={stats} tournament={tournament} />
              )}

              {activeView === "achievements" && (
                <AchievementDisplay
                  achievements={stats.achievements}
                  teams={tournament.teams}
                />
              )}

              {activeView === "path" && (
                <PathToCourt1Tracker
                  teams={tournament.teams}
                  tournament={tournament}
                />
              )}

              {activeView === "qr" && <QRCodeDisplay tournament={tournament} />}
            </div>
          </div>
        </div>
      </div>

      {/* Score Entry Modal */}
      {selectedMatch && (
        <ScoreEntry
          match={currentRound?.matches.find((m) => m.id === selectedMatch)!}
          onClose={() => setSelectedMatch(null)}
          onSubmit={updateMatchResult}
        />
      )}
    </div>
  );
};

export default TournamentDashboard;
